// Turning a palette into text someone can paste into a chat message, and back.
//
// This exists for handing a palette to someone who isn't a user of the app at
// all — a designer proposing it as a future built-in theme. Export renders the
// palette a person is looking at (saved or not) as JSON; Import reads that
// text back into the same shape the editor already knows how to hold.
//
// JSON rather than a bespoke format: it is the one syntax that needs no parser
// written for it, it is unambiguous about nesting, and pasting it into a chat
// keeps it verbatim — nothing here depends on more than that.

import type { Palette } from "../api/types";

/** The document Export produces and Import reads. */
export interface SharedTheme {
  kind: "allowance-theme";
  /** Bumped if the shape below ever needs to change. */
  version: 1;
  name: string;
  description: string;
  palette: Palette;
}

/** Renders a palette as the text Export shows and Import expects back. */
export function exportTheme(input: {
  name: string;
  description: string;
  palette: Palette;
}): string {
  const doc: SharedTheme = {
    kind: "allowance-theme",
    version: 1,
    name: input.name,
    description: input.description,
    palette: input.palette,
  };
  return JSON.stringify(doc, null, 2);
}

/** Thrown by importTheme; its message is safe to show whoever pasted the text. */
export class ThemeImportError extends Error {}

const NOT_RECOGNIZED = "That doesn't look like an exported theme.";

/**
 * Parses Export's output back into fields the editor can hold.
 *
 * Deliberately loose about anything the server will check again anyway — hex
 * grammar, stop count and ordering, angle range. This only has to get the
 * shape right so the editor can open with something to show; a genuinely bad
 * colour surfaces as a normal validation error the moment it's saved, same as
 * if it had been typed into a colour well by hand.
 */
export function importTheme(text: string): {
  name: string;
  description: string;
  palette: Palette;
} {
  let doc: unknown;
  try {
    doc = JSON.parse(text);
  } catch {
    throw new ThemeImportError(NOT_RECOGNIZED);
  }

  if (typeof doc !== "object" || doc === null) {
    throw new ThemeImportError(NOT_RECOGNIZED);
  }
  const d = doc as Record<string, unknown>;

  if (d.kind !== "allowance-theme") {
    throw new ThemeImportError(NOT_RECOGNIZED);
  }
  if (typeof d.name !== "string" || d.name.trim() === "") {
    throw new ThemeImportError("That theme has no name.");
  }
  if (!isPaletteShape(d.palette)) {
    throw new ThemeImportError("That theme is missing some of its colors.");
  }

  return {
    name: d.name,
    description: typeof d.description === "string" ? d.description : "",
    palette: d.palette,
  };
}

function isPaletteShape(p: unknown): p is Palette {
  if (typeof p !== "object" || p === null) return false;
  const obj = p as Record<string, unknown>;
  return isVariantShape(obj.light) && isVariantShape(obj.dark);
}

const SEED_KEYS = ["brand", "spend", "save", "celebrate", "surface", "text"];

function isVariantShape(v: unknown): boolean {
  if (typeof v !== "object" || v === null) return false;
  const obj = v as Record<string, unknown>;

  for (const key of SEED_KEYS) {
    if (typeof obj[key] !== "string") return false;
  }

  // cardOpacity is optional — every theme exported before it existed omits it,
  // and those have to keep importing. A present-but-wrong type is rejected
  // here rather than left to the server, since unmarshalling a string into an
  // int fails with a message about JSON rather than about the palette.
  if (obj.cardOpacity !== undefined && typeof obj.cardOpacity !== "number") {
    return false;
  }

  const bg = obj.background;
  if (typeof bg !== "object" || bg === null) return false;
  const bgObj = bg as Record<string, unknown>;
  if (typeof bgObj.angle !== "number") return false;
  if (!Array.isArray(bgObj.stops) || bgObj.stops.length < 1) return false;

  return bgObj.stops.every(
    (stop) =>
      typeof stop === "object" &&
      stop !== null &&
      typeof (stop as Record<string, unknown>).color === "string" &&
      typeof (stop as Record<string, unknown>).position === "number",
  );
}
